import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  ButtonType,
  ButtonWidget,
  TextLinkWidget
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { CommonStyles } from '../../common/react/CommonStyles';
import { InterpolatedString } from '../../common/react/components/InterpolatedString';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { imgPath } from '../../common/utilities/ImgUrl';
import { L10n } from '../../ts/L10n/L10n';
import { resumeMetricFromExplorer } from '../actions/UpdateMetricsActions';
import { ServiceLocator } from '../../app/ServiceLocator';
import { MapDispatchToProps } from '../../app/utils/ConnectUtils';
import { TooltipAnchor } from '../../app/spaces/Tooltips/TooltipAnchor';
import { HelpService } from '../../commonInfra/services/HelpService';

export interface MetricSuspendedIndicatorOwnProps {
  metricId: string;
  metricRefreshedTimestamp: string;
  userIsOwnerOrAdmin: boolean;
}

export interface MetricSuspendedIndicatorDispatchProps {
  resumeMetricFromExplorer: (metricId: string) => void;
}

export interface MetricSuspendedIndicatorAngularProps {
  translate: IReactTranslateService;
  resumeMetricHelpLink: string;
}

export type MetricSuspendedIndicatorProps = MetricSuspendedIndicatorOwnProps & MetricSuspendedIndicatorDispatchProps & MetricSuspendedIndicatorAngularProps;
export type MetricSuspendedIndicatorWithoutAngularProps = MetricSuspendedIndicatorOwnProps & MetricSuspendedIndicatorDispatchProps;

export class MetricSuspendedIndicator extends React.PureComponent<MetricSuspendedIndicatorProps> {
  public render() {
    return <TooltipAnchor tooltipContent={this.renderTooltipContent}
      useKeyboardToActivateTooltip={true}
      width={228}
      disableAutoFocus={!this.props.userIsOwnerOrAdmin}
      title={Messages.viewTooltipTitle()}>
      <img
        tabIndex={-1} // disable tabbing in datagrid cells, leaving focus managed by datagrid itself
        src={imgPath('server_alert_list_suspended.svg')}
        style={{ verticalAlign: 'middle', marginRight: '4px' }}
        data-tb-test-id="metric-suspended-icon"
      />
    </TooltipAnchor>;
  }

  private renderTooltipContent = (): JSX.Element => {
    if (this.props.userIsOwnerOrAdmin) {
      return (
        <div data-tb-test-id="metrics-suspended-indicator">
          <div>
            <InterpolatedString
              baseStringKey="metricSuspendedWithResumeTooltip"
              translate={this.props.translate}
              replacements={{
                'SUSPEND_DATE': () => <span>{L10n.localizedDate(this.props.metricRefreshedTimestamp)}</span>,
                'LEARN_MORE_LINK': () =>
                  <TextLinkWidget
                    href={this.props.resumeMetricHelpLink}
                    target="_blank"
                    testId="metrics-resume-help"
                  >
                    {Messages.learnMore()}
                  </TextLinkWidget>
              }}
            />
          </div>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId="metrics-resume"
            style={{ marginTop: CommonStyles.Sizes.MediumSpace }}
            onClick={() => this.props.resumeMetricFromExplorer(this.props.metricId)}
          >
            {Messages.metricResumeButtonLabel()}
          </ButtonWidget>
        </div>
      );
    } else {
      return <span>{Messages.metricSuspendedNoResumeTooltip()}</span>;
    }
  }
}

interface MetricSuspendedIndicatorAngularServices {
  translateService: ng.translate.ITranslateService;
}

const MetricSuspendedIndicatorAngular = angularProviderHOC<MetricSuspendedIndicatorWithoutAngularProps, MetricSuspendedIndicatorAngularProps, MetricSuspendedIndicatorAngularServices>({
  getAngularServicesWithServiceLocator: () => ({
    translateService: ServiceLocator.get<ng.translate.ITranslateService>('$translate'),
  }),
  selectPropsFromAngularServices: (angularServices) => ({
    translate: angularServices.translateService.instant,
    resumeMetricHelpLink: HelpService.getServerHelpUrlByCategory('metrics-suspend'),
  })
})(MetricSuspendedIndicator);

const mapDispatchToProps: MapDispatchToProps<MetricSuspendedIndicatorDispatchProps> = (dispatch) => bindActionCreators({
  resumeMetricFromExplorer
}, dispatch);

export const ConnectedMetricSuspendedIndicator = connect(undefined, mapDispatchToProps)(MetricSuspendedIndicatorAngular);

